import { forwardRef } from "react";

import { Icon } from "@/components/icons/Icon";
import { getInitials } from "@/utils/getInitials";
import { getRandomTailwindColor } from "@/utils/tailwindColors";
import { twMerge } from "@/utils/twMerge";

import { Dropdown } from "../sidebar/Dropdown";

import type { DropdownItem } from "../sidebar/Dropdown";
import type { TailwindColor } from "@/utils/tailwindColors";
import type { ComponentProps } from "react";

type ProfileRootProps = {
  menuItems?: DropdownItem[];
  dropdownClassName?: string;
} & ComponentProps<"div">;

function ProfileRoot({ menuItems, className, dropdownClassName, ...rest }: ProfileRootProps) {
  const buttonComponent = (
    <div className={twMerge("flex items-center justify-end gap-4", className)} {...rest} />
  );

  if (!menuItems) {
    return buttonComponent;
  }

  return (
    <Dropdown
      className={dropdownClassName}
      anchor="bottom end"
      toggleButtonComponent={buttonComponent}
      items={menuItems}
    />
  );
}

type ProfileUserProps = {
  name?: string | null;
  email?: string | null;
  username?: string | null;
  menuItems?: { title: string; onClick: () => void }[];
} & ComponentProps<"div">;

const ProfileUser = forwardRef<HTMLDivElement, ProfileUserProps>(function ProfileUser(
  { name, email, username, className, ...rest }: ProfileUserProps,
  ref,
) {
  return (
    <div {...rest} ref={ref} className={twMerge("flex flex-col", className)}>
      <div className="text-[16px] leading-[20px]">
        {name || <span className="text-gray-500">{email}</span>}
      </div>
      {username && <div className="text-gray-500 text-[14px] leading-[18px]">@{username}</div>}
    </div>
  );
});

export type ProfileUserpicProps = {
  name: string;
  color?: TailwindColor;
  src?: string | null;
  setColorFrom?: string;
  size?: "sm" | "lg";
  badge?: React.ReactNode;
};

function ProfileUserpic({
  name,
  src,
  color = "gray",
  className,
  setColorFrom,
  size = "sm",
  badge,
  ...rest
}: ProfileUserpicProps & ComponentProps<"div">) {
  const initials = getInitials(name);
  const borderColor = setColorFrom ? getRandomTailwindColor(setColorFrom) : color;
  const width = size === "sm" ? 32 : 100;

  const userpic = (
    <div
      className={twMerge(
        "overflow-hidden",
        "font-mono text-size-18 w-8 h-8 leading-0 flex items-center justify-center rounded-full bg-document-bg shadow-node-outline select-none",
        "sm:w-10 sm:h-10",
        size === "lg" && "w-[100px] h-[100px] text-[48px]",
        !src && `border border-${borderColor}-500`,
        className,
      )}
      {...rest}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          className="w-full h-full object-cover rounded-full text-document-text bg-white"
          width={width}
          height={width}
        />
      ) : (
        initials
      )}
    </div>
  );

  if (!badge) return userpic;

  return (
    <div className={twMerge("relative self-start shrink-0", className)}>
      {userpic}
      <span className="absolute -right-[7px] -bottom-[7px] flex items-center justify-center rounded-full">
        {badge}
      </span>
    </div>
  );
}

function CorcaLogoIcon(props: ComponentProps<"svg">) {
  return <Icon {...props} name="CorcaLogo" className="w-10 h-10" />;
}

export {
  ProfileUserpic as Userpic,
  CorcaLogoIcon as Logo,
  ProfileRoot as Root,
  ProfileUser as User,
};
